import { useState } from "react";
import { useTranslation } from "react-i18next";
import { api, formatApiError } from "../lib/api";
import { getTrackingContext } from "../lib/attribution";
import { trackConversion } from "../lib/gtag";
import { toast } from "sonner";
import { Send, Upload, Loader2, Phone, ShieldCheck, X } from "lucide-react";
import { Reveal, MaskedLine } from "./motion/Reveal";

const WORK_KEYS = ["fault", "panel", "circuit", "lighting", "nen", "intercom", "data", "maintenance", "other"];
// Canonical Dutch values sent to backend (backend stays language-agnostic)
const WORK_CANONICAL = {
  fault: "Elektrische storing",
  panel: "Groepenkast vervangen / uitbreiden",
  circuit: "Nieuwe groep / krachtstroom",
  lighting: "Verlichting installeren",
  nen: "NEN 3140 inspectie",
  intercom: "Intercominstallatie",
  data: "Data- / netwerkbekabeling",
  maintenance: "Onderhoud",
  other: "Anders",
};

const initial = {
  name: "", phone: "", email: "", address: "",
  work_type: "", description: "", preferred_date: "",
};

export default function QuoteForm() {
  const { t } = useTranslation();
  const [form, setForm] = useState(initial);
  const [photo, setPhoto] = useState(null);
  const [photoPreview, setPhotoPreview] = useState("");
  const [submitting, setSubmitting] = useState(false);

  const onChange = (e) => setForm({ ...form, [e.target.name]: e.target.value });

  const onPhotoChange = (e) => {
    const f = e.target.files?.[0] || null;
    if (f && f.size > 8 * 1024 * 1024) {
      toast.error(t("quote.validation.photo_too_large", { size: (f.size / (1024 * 1024)).toFixed(1) }));
      e.target.value = "";
      return;
    }
    setPhoto(f);
    if (f) {
      const reader = new FileReader();
      reader.onload = () => setPhotoPreview(reader.result);
      reader.readAsDataURL(f);
    } else {
      setPhotoPreview("");
    }
  };

  const clearPhoto = () => {
    setPhoto(null);
    setPhotoPreview("");
    const input = document.getElementById("photo-input");
    if (input) input.value = "";
  };

  const onSubmit = async (e) => {
    e.preventDefault();
    if (!form.name || !form.phone || !form.email || !form.work_type || !form.description) {
      toast.error(t("quote.validation.required"));
      return;
    }
    setSubmitting(true);
    try {
      const fd = new FormData();
      Object.entries(form).forEach(([k, v]) => fd.append(k, v));
      const ctx = getTrackingContext();
      Object.entries(ctx).forEach(([k, v]) => { if (v) fd.append(k, String(v)); });
      if (photo) fd.append("photo", photo);
      await api.post("/quotes", fd, { headers: { "Content-Type": "multipart/form-data" } });
      trackConversion(
        "quote_submit",
        { work_type: form.work_type, value: 50, currency: "EUR" },
        {
          email: form.email,
          phone: form.phone,
          name: form.name,
          address: form.address,
          country: "NL",
        }
      );
      toast.success(t("quote.toast.success"));
      setForm(initial);
      clearPhoto();
    } catch (err) {
      toast.error(formatApiError(err.response?.data?.detail) || t("quote.toast.failed"));
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <section id="offerte" data-testid="quote-section" className="section bg-[#F4F4F5]">
      <div className="max-w-6xl mx-auto px-4 sm:px-6 md:px-10 grid lg:grid-cols-12 gap-8 md:gap-12">
        <div className="lg:col-span-5">
          <Reveal>
            <div className="flex items-center gap-3 mb-4">
              <span className="accent-bar" />
              <span className="text-[#FF5A1F] uppercase font-bold tracking-widest text-xs">
                {t("quote.eyebrow")}
              </span>
            </div>
          </Reveal>
          <h2 className="font-display text-4xl md:text-5xl lg:text-6xl text-[#0A1128] mb-6 leading-[1.02]">
            <span className="block overflow-hidden"><MaskedLine text={t("quote.title_1")} wordDelay={0.06} /></span>
            <span className="block overflow-hidden"><MaskedLine text={t("quote.title_2")} wordDelay={0.05} delay={0.15} /></span>
          </h2>
          <Reveal delay={0.15}>
            <p className="text-base md:text-lg text-slate-700 leading-relaxed mb-8">
              {t("quote.subtitle")}{" "}
              <a href="tel:0684099561" className="text-[#FF5A1F] font-bold hover:underline whitespace-nowrap">0684099561</a>.
            </p>
          </Reveal>

          <div className="space-y-4">
            <div className="bg-[#0A1128] text-white p-5 md:p-6 border-l-4 border-[#FF5A1F] flex items-start gap-4">
              <ShieldCheck className="w-6 h-6 text-[#FF5A1F] shrink-0 mt-1" />
              <div>
                <p className="font-display text-lg md:text-xl mb-1">{t("quote.promise_title")}</p>
                <p className="text-white/80 text-sm">{t("quote.promise_desc")}</p>
              </div>
            </div>
            <a href="tel:0684099561" className="hidden lg:flex items-center gap-3 bg-white p-5 border border-slate-200 hover:border-[#FF5A1F] transition-colors group">
              <span className="w-11 h-11 bg-[#FF5A1F] flex items-center justify-center shrink-0">
                <Phone className="w-5 h-5 text-white" strokeWidth={2.4} />
              </span>
              <div>
                <div className="text-xs uppercase tracking-widest text-slate-500 font-bold">{t("quote.direct_call")}</div>
                <div className="font-display text-lg text-[#0A1128] group-hover:text-[#FF5A1F] transition-colors">0684099561</div>
              </div>
            </a>
          </div>
        </div>

        <Reveal className="lg:col-span-7" y={30}>
          <form onSubmit={onSubmit} data-testid="quote-form" className="bg-white p-5 sm:p-8 md:p-10 border border-slate-200 shadow-[8px_8px_0_0_rgba(10,17,40,0.06)]">
            <div className="grid sm:grid-cols-2 gap-4 sm:gap-5">
              <div>
                <label className="field-label" htmlFor="q-name">{t("quote.fields.name")} *</label>
                <input id="q-name" data-testid="q-name" required autoComplete="name" name="name" value={form.name} onChange={onChange} className="field-input" placeholder={t("quote.fields.name_ph")} />
              </div>
              <div>
                <label className="field-label" htmlFor="q-phone">{t("quote.fields.phone")} *</label>
                <input id="q-phone" data-testid="q-phone" required type="tel" inputMode="tel" autoComplete="tel" name="phone" value={form.phone} onChange={onChange} className="field-input" placeholder={t("quote.fields.phone_ph")} />
              </div>
            </div>

            <div className="grid sm:grid-cols-2 gap-4 sm:gap-5 mt-4 sm:mt-5">
              <div>
                <label className="field-label" htmlFor="q-email">{t("quote.fields.email")} *</label>
                <input id="q-email" data-testid="q-email" required type="email" inputMode="email" autoComplete="email" name="email" value={form.email} onChange={onChange} className="field-input" placeholder={t("quote.fields.email_ph")} />
              </div>
              <div>
                <label className="field-label" htmlFor="q-address">{t("quote.fields.address")}</label>
                <input id="q-address" data-testid="q-address" autoComplete="street-address" name="address" value={form.address} onChange={onChange} className="field-input" placeholder={t("quote.fields.address_ph")} />
              </div>
            </div>

            <div className="grid sm:grid-cols-2 gap-4 sm:gap-5 mt-4 sm:mt-5">
              <div>
                <label className="field-label" htmlFor="q-work">{t("quote.fields.work_type")} *</label>
                <select id="q-work" data-testid="q-work" required name="work_type" value={form.work_type} onChange={onChange} className="field-input appearance-none bg-white bg-[url('data:image/svg+xml;utf8,%3Csvg%20xmlns%3D%22http%3A%2F%2Fwww.w3.org%2F2000%2Fsvg%22%20width%3D%2212%22%20height%3D%228%22%20viewBox%3D%220%200%2012%208%22%3E%3Cpath%20fill%3D%22%230A1128%22%20d%3D%22M6%208L0%200h12z%22%2F%3E%3C%2Fsvg%3E')] bg-no-repeat bg-[right_16px_center] pr-10">
                  <option value="">{t("quote.fields.select")}</option>
                  {WORK_KEYS.map((k) => (
                    <option key={k} value={WORK_CANONICAL[k]}>{t(`quote.work_types.${k}`)}</option>
                  ))}
                </select>
              </div>
              <div>
                <label className="field-label" htmlFor="q-date">{t("quote.fields.date")}</label>
                <input id="q-date" data-testid="q-date" type="date" name="preferred_date" value={form.preferred_date} onChange={onChange} className="field-input" />
              </div>
            </div>

            <div className="mt-4 sm:mt-5">
              <label className="field-label" htmlFor="q-desc">{t("quote.fields.description")} *</label>
              <textarea id="q-desc" data-testid="q-desc" required rows={5} name="description" value={form.description} onChange={onChange} className="field-input resize-y" placeholder={t("quote.fields.description_ph")} style={{ minHeight: 120 }} />
            </div>

            <div className="mt-4 sm:mt-5">
              <label className="field-label" htmlFor="photo-input">{t("quote.fields.photo")}</label>
              {photoPreview ? (
                <div className="relative border border-slate-200 bg-slate-50 p-3 flex items-center gap-3">
                  <img src={photoPreview} alt="preview" className="w-16 h-16 object-cover" />
                  <div className="flex-1 min-w-0">
                    <div className="text-sm text-[#0A1128] font-semibold truncate">{photo?.name}</div>
                    <div className="text-xs text-slate-500">{t("quote.fields.remove_photo")}</div>
                  </div>
                  <button type="button" onClick={clearPhoto} className="w-9 h-9 flex items-center justify-center bg-white border border-slate-300 hover:border-red-500 hover:text-red-600 transition-colors" aria-label="remove">
                    <X className="w-4 h-4" />
                  </button>
                </div>
              ) : (
                <label htmlFor="photo-input" className="flex items-center gap-3 border-2 border-dashed border-slate-300 hover:border-[#FF5A1F] px-4 py-5 cursor-pointer transition-colors bg-slate-50 min-h-[64px]">
                  <Upload className="w-5 h-5 text-[#FF5A1F] shrink-0" />
                  <span className="text-sm text-slate-600 truncate">{t("quote.fields.photo_upload")}</span>
                </label>
              )}
              <input id="photo-input" data-testid="q-photo" type="file" accept="image/*" className="hidden" onChange={onPhotoChange} />
            </div>

            <button type="submit" data-testid="q-submit" disabled={submitting}
                    className="btn-primary btn-lg w-full mt-6 sm:mt-8 disabled:opacity-70 disabled:cursor-not-allowed">
              {submitting
                ? (<><Loader2 className="w-5 h-5 animate-spin" /> {t("quote.submitting")}</>)
                : (<><Send className="w-5 h-5" /> {t("quote.submit")}</>)}
            </button>
            <p className="text-xs text-slate-500 text-center mt-4">
              <ShieldCheck className="w-3.5 h-3.5 inline text-[#FF5A1F] mr-1 -mt-0.5" />
              {t("quote.privacy_note")}
            </p>
          </form>
        </Reveal>
      </div>
    </section>
  );
}
